import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Minus, Square, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export type WindowedCardMode = 'minimized' | 'maximized';

interface WindowControlsProps {
  mode: WindowedCardMode;
  onMinimize: () => void;
  onMaximize: () => void;
  onClose?: () => void;
}

export const WindowControls = ({
  mode,
  onMinimize,
  onMaximize,
  onClose,
}: WindowControlsProps) => {
  const baseBtn =
    'inline-flex items-center justify-center h-[20px] w-[20px] rounded-[4px] transition-colors';
  const iconCls = 'w-[12px] h-[12px]';
  return (
    <div className="flex items-center gap-[2px]">
      <button
        type="button"
        aria-label="Minimize"
        title="Minimize"
        onClick={onMinimize}
        disabled={mode === 'minimized'}
        className={cn(
          baseBtn,
          mode === 'minimized'
            ? 'text-[var(--fg-4)] cursor-default'
            : 'text-[var(--fg-3)] hover:text-[var(--fg)] hover:bg-[var(--bg-alt)] cursor-pointer',
        )}
      >
        <Minus className={iconCls} strokeWidth={1.8} />
      </button>
      <button
        type="button"
        aria-label="Maximize"
        title="Maximize"
        onClick={onMaximize}
        disabled={mode === 'maximized'}
        className={cn(
          baseBtn,
          mode === 'maximized'
            ? 'text-[var(--fg-4)] cursor-default'
            : 'text-[var(--fg-3)] hover:text-[var(--fg)] hover:bg-[var(--bg-alt)] cursor-pointer',
        )}
      >
        <Square className={iconCls} strokeWidth={1.6} />
      </button>
      {onClose && (
        <button
          type="button"
          aria-label="Close"
          title="Close"
          onClick={onClose}
          className={cn(
            baseBtn,
            'text-[var(--fg-3)] hover:text-[var(--fg)] hover:bg-[var(--bg-alt)] cursor-pointer',
          )}
        >
          <X className={iconCls} strokeWidth={1.8} />
        </button>
      )}
    </div>
  );
};

export interface WindowedCardProps {
  title?: ReactNode;
  mode: WindowedCardMode;
  onModeChange: (mode: WindowedCardMode) => void;
  onClose?: () => void;
  minimizedContent: ReactNode;
  maximizedContent: ReactNode;
  /** Cap on minimized width as a fraction of the viewport (e.g. 0.2 = 20vw). */
  minimizedMaxWidthVw?: number;
  /** Width applied when maximized. */
  maximizedWidthClass?: string;
  /** Outer position class. Defaults to centered. */
  positionClass?: string;
  /** Override the inner chrome (border/bg/shadow) class. */
  chromeClass?: string;
}

export const WindowedCard = ({
  title,
  mode,
  onModeChange,
  onClose,
  minimizedContent,
  maximizedContent,
  minimizedMaxWidthVw = 0.2,
  maximizedWidthClass = 'w-[420px] max-w-[calc(100vw-32px)]',
  positionClass = 'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[10050]',
  chromeClass = 'rounded-lg border border-solid border-[var(--hair)] bg-[var(--surface)]/95 text-[var(--fg-2)] shadow-lg backdrop-blur-sm',
}: WindowedCardProps) => {
  const isMin = mode === 'minimized';
  const cardRef = useRef<HTMLDivElement | null>(null);
  // null position means "use positionClass"; once dragged, switch to absolute coords.
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ dx: number; dy: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  const onHeaderMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Don't start a drag if clicking a control button.
      if ((e.target as HTMLElement).closest('button')) return;
      const card = cardRef.current;
      if (!card) return;
      const rect = card.getBoundingClientRect();
      dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
      // Lock to current absolute position before drag begins so the card doesn't
      // jump if positionClass uses transforms.
      setPos({ x: rect.left, y: rect.top });
      setDragging(true);
      e.preventDefault();
    },
    [],
  );

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const offset = dragRef.current;
      const card = cardRef.current;
      if (!offset || !card) return;
      const rect = card.getBoundingClientRect();
      const maxX = window.innerWidth - rect.width;
      const maxY = window.innerHeight - rect.height;
      const x = Math.max(0, Math.min(maxX, e.clientX - offset.dx));
      const y = Math.max(0, Math.min(maxY, e.clientY - offset.dy));
      setPos({ x, y });
    };
    const onUp = () => {
      setDragging(false);
      dragRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging]);

  const dragged = pos !== null;
  const outerStyle: React.CSSProperties = dragged
    ? { position: 'fixed', left: pos!.x, top: pos!.y, zIndex: 10050 }
    : {};
  if (isMin) {
    outerStyle.maxWidth = `${Math.round(minimizedMaxWidthVw * 100)}vw`;
  }

  return (
    <div
      ref={cardRef}
      className={dragged ? '' : positionClass}
      style={outerStyle}
    >
      <div
        className={cn(
          chromeClass,
          'font-mono text-[11px] leading-snug',
          !isMin && maximizedWidthClass,
          isMin && 'inline-block min-w-[200px]',
        )}
      >
        <div
          onMouseDown={onHeaderMouseDown}
          className={cn(
            'flex items-center justify-between gap-2 px-3 py-1.5',
            'border-b border-solid border-[var(--hair)]',
            'select-none',
            dragging ? 'cursor-grabbing' : 'cursor-grab',
          )}
        >
          <div className="min-w-0 truncate text-[10px] uppercase tracking-[0.14em] text-[var(--fg-3)]">
            {title}
          </div>
          <WindowControls
            mode={mode}
            onMinimize={() => onModeChange('minimized')}
            onMaximize={() => onModeChange('maximized')}
            onClose={onClose}
          />
        </div>
        <div className="px-3 py-2">{isMin ? minimizedContent : maximizedContent}</div>
      </div>
    </div>
  );
};
